import { Autocomplete, Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextInterface } from '../IContext';

export type ArticleType = 'narrative' | 'letter' | 'review';
type LanguageVariant = 'traditional' | 'simplified';
type QuestionType = 'mc';

class ReadingComprehensionContextClass implements ReadingComprehensionContext {
    type: 'readingComprehension';
    topic?: string;
    subtopic?: string;
    article_type?: ArticleType;
    language_variant?: LanguageVariant;
    question_count?: number;
    question_type?: QuestionType;

    constructor(context: Pick<ReadingComprehensionContext, 'type' | 'topic' | 'subtopic' | 'article_type' | 'language_variant' | 'question_count' | 'question_type'>) {
        this.type = context.type;
        this.topic = context.topic;
        this.subtopic = context.subtopic;
        this.article_type = context.article_type;
        this.language_variant = context.language_variant;
        this.question_count = context.question_count;
        this.question_type = context.question_type;
    }

    isSufficient(): boolean {
        return !!this.topic && !!this.subtopic && !!this.article_type && !!this.language_variant && !!this.question_count && !!this.question_type;
    }
}

export interface ReadingComprehensionContext extends ContextInterface {
    type: 'readingComprehension';
    topic?: string;
    subtopic?: string;
    article_type?: ArticleType;
    language_variant?: LanguageVariant;
    question_count?: number;
    question_type?: QuestionType;
}

export const RCPanel = ({ value, onChange }: { value: ReadingComprehensionContext; onChange?: (value: ReadingComprehensionContext) => void }) => {
    const { t } = useTranslation();

    const [topic, setTopic] = useState<string>(value?.topic || '');
    const [subtopic, setSubtopic] = useState<string>(value?.subtopic || '');
    const [articleType, setArticleType] = useState<ArticleType>(value?.article_type || 'narrative');
    const [questionType, setQuestionType] = useState<QuestionType>(value?.question_type || 'mc');
    const [languageVariant, setLanguageVariant] = useState<LanguageVariant>(value?.language_variant || 'traditional');
    const [questionCount, setQuestionCount] = useState<number>(value?.question_count || 1);

    const topicOptions = [
        // sample themes
        {
            category: 'IGCSE',
            title: 'Everyday activities',
            sub: [{ title: 'Time expressions' }, { title: 'Food and drink' }, { title: 'The human body and health' }, { title: 'Travel and transport' }],
        },
        {
            category: 'IGCSE',
            title: 'Personal and social life',
            sub: [{ title: 'Self, family and friends' }, { title: 'Home life' }, { title: 'Colours' }, { title: 'Clothes and accessories' }, { title: 'Leisure time' }],
        },
        {
            category: 'IGCSE',
            title: 'The world around us',
            sub: [
                { title: 'People and places' },
                { title: 'The natural world, the environment, the climate and the weather' },
                { title: 'Communications and technology' },
                { title: 'The built environment' },
                { title: 'Measurements' },
            ],
        },
        {
            category: 'IGCSE',
            title: 'The world of work',
            sub: [{ title: 'Education' }, { title: 'Work' }],
        },
        {
            category: 'IGCSE',
            title: 'The international world',
            sub: [{ title: 'Countries, nationalities and languages' }, { title: 'Culture' }],
        },
        { category: t('experiment.generator.question.label.sample'), title: '運動' },
        { category: t('experiment.generator.question.label.sample'), title: '動物' },
        { category: t('experiment.generator.question.label.sample'), title: '交通工具' },
        { category: t('experiment.generator.question.label.sample'), title: '天氣' },
        { category: t('experiment.generator.question.label.sample'), title: '食物' },
        { category: t('experiment.generator.question.label.sample'), title: '節日' },
        { category: t('experiment.generator.question.label.sample'), title: '地理' },
        { category: t('experiment.generator.question.label.sample'), title: '科技' },
        { category: t('experiment.generator.question.label.sample'), title: '藝術' },
    ];

    return (
        <Stack spacing={2}>
            <Box>
                <Autocomplete
                    aria-label='rc topic'
                    fullWidth
                    freeSolo
                    options={topicOptions}
                    groupBy={(option) => option.category}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                    value={topic}
                    onChange={(_, newValue) => {
                        const topicValue = typeof newValue === 'string' ? newValue : newValue?.title || '';
                        setTopic(topicValue);
                        onChange?.(
                            new ReadingComprehensionContextClass({
                                type: 'readingComprehension',
                                topic: topicValue,
                                subtopic,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );

                        setSubtopic('');
                        onChange?.(
                            new ReadingComprehensionContextClass({
                                type: 'readingComprehension',
                                topic,
                                subtopic: '',
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    onInputChange={(_, newInputValue) => {
                        setTopic(newInputValue);
                        onChange?.(
                            new ReadingComprehensionContextClass({
                                type: 'readingComprehension',
                                topic: newInputValue,
                                subtopic,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    renderInput={(params) => <TextField {...params} placeholder={t('experiment.generator.question.label.topic')} label={t('experiment.generator.question.label.topic')} />}
                />
            </Box>
            <Box>
                <Autocomplete
                    aria-label='rc subtopic'
                    fullWidth
                    freeSolo
                    options={topicOptions.find((opt) => opt.title === topic)?.sub || []}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                    value={subtopic}
                    onChange={(_, newValue) => {
                        const subtopicValue = typeof newValue === 'string' ? newValue : newValue?.title || '';
                        setSubtopic(subtopicValue);
                        onChange?.(
                            new ReadingComprehensionContextClass({
                                type: 'readingComprehension',
                                topic,
                                subtopic: subtopicValue,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    onInputChange={(_, newInputValue) => {
                        setSubtopic(newInputValue);
                        onChange?.(
                            new ReadingComprehensionContextClass({
                                type: 'readingComprehension',
                                topic,
                                subtopic: newInputValue,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    renderInput={(params) => <TextField {...params} placeholder={t('experiment.generator.question.label.subtopic')} label={t('experiment.generator.question.label.subtopic')} />}
                />
            </Box>
            <Box>
                <FormControl fullWidth>
                    <InputLabel>{t('experiment.generator.question.label.articleType')}</InputLabel>
                    <Select
                        label={t('experiment.generator.question.label.articleType')}
                        aria-label='rc article type'
                        value={articleType}
                        onChange={(e) => {
                            setArticleType(e.target.value as ArticleType);
                            onChange?.(
                                new ReadingComprehensionContextClass({
                                    type: 'readingComprehension',
                                    topic,
                                    subtopic,
                                    article_type: e.target.value as ArticleType,
                                    language_variant: languageVariant,
                                    question_count: questionCount,
                                    question_type: questionType,
                                }),
                            );
                        }}
                    >
                        <MenuItem value='narrative'>{t('experiment.generator.question.articleType.narrative')}</MenuItem>
                        <MenuItem value='letter'>{t('experiment.generator.question.articleType.letter')}</MenuItem>
                        <MenuItem value='review'>{t('experiment.generator.question.articleType.review')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box>
                <FormControl fullWidth>
                    <InputLabel>{t('experiment.generator.question.label.languageVariant')}</InputLabel>
                    <Select
                        label={t('experiment.generator.question.label.languageVariant')}
                        value={languageVariant}
                        onChange={(e) => {
                            setLanguageVariant(e.target.value as LanguageVariant);
                            onChange?.(
                                new ReadingComprehensionContextClass({
                                    type: 'readingComprehension',
                                    topic,
                                    subtopic,
                                    article_type: articleType,
                                    language_variant: e.target.value as LanguageVariant,
                                    question_count: questionCount,
                                    question_type: questionType,
                                }),
                            );
                        }}
                    >
                        <MenuItem value='traditional'>{t('experiment.generator.question.languageVariant.traditional')}</MenuItem>
                        <MenuItem value='simplified'>{t('experiment.generator.question.languageVariant.simplified')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box>
                <TextField
                    label={t('experiment.generator.question.label.questionCount')}
                    fullWidth
                    type='number'
                    aria-label='rc question count'
                    placeholder={t('experiment.generator.question.label.questionCount')}
                    value={questionCount === 0 ? '' : questionCount}
                    slotProps={{
                        htmlInput: {
                            min: 1,
                            max: 10,
                        },
                    }}
                    onChange={(e) => {
                        let count = 0;
                        if (Number(e.target.value) <= 0) {
                            setQuestionCount(count);
                        } else {
                            count = Number(e.target.value);
                            setQuestionCount(count > 10 ? 10 : count);
                        }
                        onChange?.(
                            new ReadingComprehensionContextClass({
                                type: 'readingComprehension',
                                topic,
                                subtopic,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: count,
                                question_type: questionType,
                            }),
                        );
                    }}
                />
            </Box>
            <Box>
                <FormControl fullWidth>
                    <InputLabel>{t('experiment.generator.question.label.questionFormat')}</InputLabel>
                    <Select
                        label={t('experiment.generator.question.label.questionFormat')}
                        value={questionType}
                        onChange={(e) => {
                            setQuestionType(e.target.value as QuestionType);
                            onChange?.(
                                new ReadingComprehensionContextClass({
                                    type: 'readingComprehension',
                                    topic,
                                    subtopic,
                                    article_type: articleType,
                                    language_variant: languageVariant,
                                    question_count: questionCount,
                                    question_type: e.target.value as QuestionType,
                                }),
                            );
                        }}
                    >
                        <MenuItem value='mc'>{t('experiment.generator.question.type.mc')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Stack>
    );
};
